import type { DocumentPickerAsset } from "expo-document-picker";
import type { ImagePickerAsset } from "expo-image-picker";
import { useCallback, useState } from "react";
import { Paperclip as PaperclipIcon, Upload as UploadIcon } from "@tamagui/lucide-icons";
import { Body1, FilePickerButton, hairline, ImagePickerButton, isWeb, Separator, Title5, UploadButton, XStack, YStack } from "@medbillai/ui";
import { generateFileName, isSupportedImageType } from "@medbillai/utils/images";
import { analyticsService } from "../../lib/analytics";
import type { UploadError, UploadFile } from "../../provider/documents";
import { AdaptiveDialogSheet } from "../AdaptiveDialogSheet";
import { CameraPickerButton } from "./components/CameraPickerButton";
import { openScanbot } from "./utils/open-scanbot/openScanbot";
const Divider = () => <XStack px="$4.5" data-sentry-element="XStack" data-sentry-component="Divider" data-sentry-source-file="UploadDocumentSheet.tsx">
    <Separator w="100%" backgroundColor="$strokeSecondary" height={hairline} data-sentry-element="Separator" data-sentry-source-file="UploadDocumentSheet.tsx" />
  </XStack>;
export type UploadDocumentSheetProps = {
  open: boolean;
  onUploadError: (error: UploadError) => void;
  onOpenChange: (open: boolean) => void;
  onCameraPermissionCheckStart: () => void;
  onCameraPermissionCheckComplete: () => void;
  onCameraPermissionDenied?: () => void;
  onFilesLoaded: (files: UploadFile[]) => Promise<void>;
  cleanupBeforeUpload?: () => void;
  title?: string;
  subtitle?: string;
};
export const UploadDocumentSheet = ({
  open,
  onUploadError,
  onOpenChange,
  onCameraPermissionCheckStart,
  onCameraPermissionCheckComplete,
  onCameraPermissionDenied,
  onFilesLoaded,
  cleanupBeforeUpload,
  title = "Upload",
  subtitle
}: UploadDocumentSheetProps) => {
  const [useCamera, setUseCamera] = useState<boolean>(false);
  const openCamera = async () => {
    try {
      setUseCamera(true);
      const scannedImages = await openScanbot();
      if (scannedImages.status === "CANCELED") {
        return;
      }

      // Only proceed if we have valid images
      if (scannedImages.images.length > 0) {
        multiPhotoUpload(scannedImages.images);
      } else {
        throw new Error("No valid images found");
      }
    } catch (error) {
      console.error("Error during document scanning:", error);
      onUploadError({
        error: error instanceof Error ? error : new Error(String(error))
      });
    } finally {
      setUseCamera(false);
    }
  };
  const onError = useCallback((error: unknown) => {
    if (error instanceof Error) {
      onUploadError({
        error
      });
      return;
    }
    onUploadError({
      error: new Error("Unknown error")
    });
  }, [onUploadError]);
  const multiPhotoUpload = useCallback((images: {
    id: string;
    path: string;
  }[]) => {
    const handleUpload = async () => {
      if (images.length === 0) {
        return;
      }
      try {
        const uploadFiles = await Promise.all(images.reverse().map(async (image, idx) => {
          const response = await fetch(image.path);
          const blob = await response.blob();
          if (!isSupportedImageType(blob.type)) {
            onUploadError({
              message: "This file type is not currently supported."
              // No error field - so we don't log an error to Sentry.
            });
            throw new Error("Unsupported file type");
          }
          const filename = `${idx}-${generateFileName(blob.type)}`;
          return {
            name: filename,
            sizeBytes: blob.size,
            contentType: blob.type,
            blob
          } as UploadFile;
        }));
        void onFilesLoaded(uploadFiles);
      } catch (error) {
        onError(error);
      }
    };
    // call cleanup if necessary
    cleanupBeforeUpload?.();
    void handleUpload();
    analyticsService.trackEvent("ClickedUpload", {
      type: "CAMERA"
    });
  }, [onError, onFilesLoaded, onUploadError, cleanupBeforeUpload]);
  const onImageSelected = (images: ImagePickerAsset[], setButtonLoading: (loading: boolean) => void): void => {
    const handleUpload = async () => {
      if (images.length === 0) {
        return;
      }
      try {
        const uploadFiles = await Promise.all(images.map(async (image, idx) => {
          const response = await fetch(image.uri);
          const blob = await response.blob();
          if (!isSupportedImageType(blob.type)) {
            onUploadError({
              message: "This file type is not currently supported."
              // No error field - so we don't log an error to Sentry.
            });
            throw new Error("Unsupported file type");
          }
          const filename = `${idx}-${generateFileName(blob.type)}`;
          return {
            name: filename,
            sizeBytes: blob.size,
            contentType: blob.type,
            blob
          } as UploadFile;
        }));
        void onFilesLoaded(uploadFiles);
      } catch (error) {
        onError(error);
      }
    };
    // call cleanup if necessary
    cleanupBeforeUpload?.();
    void handleUpload();
    setButtonLoading(false);
    analyticsService.trackEvent("ClickedUpload", {
      type: "GALLERY"
    });
  };
  const onFilesSelected = (files: DocumentPickerAsset[], setButtonLoading: (loading: boolean) => void) => {
    const handleUpload = async () => {
      if (files.length === 0) {
        return;
      }
      try {
        const uploadFiles: UploadFile[] = [];
        for (const file of files) {
          const response = await fetch(file.uri);
          const blob = await response.blob();
          uploadFiles.push({
            name: file.name,
            sizeBytes: file.size,
            contentType: blob.type,
            blob
          });
        }
        void onFilesLoaded(uploadFiles);
      } catch (error) {
        onError(error);
      }
    };
    // call cleanup if necessary
    cleanupBeforeUpload?.();
    // Close and cleanup document sheet
    void handleUpload();
    setButtonLoading(false);
    analyticsService.trackEvent("ClickedUpload", {
      type: "FILE"
    });
  };
  return <AdaptiveDialogSheet open={open} onOpenChange={onOpenChange} dialogContentProps={{
    minWidth: 400,
    maxWidth: 440,
    minHeight: 200
  }} title={subtitle ? "" : title} hideTitle={!!subtitle} snapPointsMode="fit" sheetProps={{
    modal: false
  }} data-sentry-element="AdaptiveDialogSheet" data-sentry-component="UploadDocumentSheet" data-sentry-source-file="UploadDocumentSheet.tsx">
      {subtitle ? <YStack gap="$sp2" alignItems="center" paddingBottom="$sp1">
          <Title5>{title}</Title5>
          <Body1 color="$textSecondary" textAlign="center">
            {subtitle}
          </Body1>
        </YStack> : null}
      <YStack ai="center" data-sentry-element="YStack" data-sentry-source-file="UploadDocumentSheet.tsx">
        {!isWeb ? <>
            <CameraPickerButton isLoading={useCamera} onPermissionCheckStart={onCameraPermissionCheckStart} onPermissionCheckComplete={onCameraPermissionCheckComplete} onPermissionDenied={onCameraPermissionDenied} setIsLoading={setUseCamera} openCamera={openCamera} />
            <Divider />
            <ImagePickerButton onImageSelected={onImageSelected} aria-label="Add photo">
              {isLoading => {
            return <UploadButton icon={UploadIcon} isLoading={isLoading} title="Upload photos" description="Upload a picture from your device" />;
          }}
            </ImagePickerButton>
            <Divider />
            <FilePickerButton onFilesSelected={onFilesSelected} aria-label="Upload Document">
              {isLoading => {
            return <UploadButton icon={PaperclipIcon} isLoading={isLoading} title="Choose files" description="Choose from the files on your device" />;
          }}
            </FilePickerButton>
          </> : <>
            <ImagePickerButton onImageSelected={onImageSelected} aria-label="Add photos">
              {isLoading => {
            return <UploadButton icon={UploadIcon} isLoading={isLoading} title="Upload photos" description="Select multiple images from your device" />;
          }}
            </ImagePickerButton>
            <Divider />
            <FilePickerButton onFilesSelected={onFilesSelected} accept={["application/pdf"]} multiple={false} aria-label="Upload Document">
              {isLoading => {
            return <UploadButton icon={PaperclipIcon} isLoading={isLoading} title="Choose file" description="Select a PDF file" />;
          }}
            </FilePickerButton>
          </>}
      </YStack>
    </AdaptiveDialogSheet>;
};